/**
 * Método para gerar o padrão da mascara de coordenadas
 * em graus decimais, e ser usado em conjunto com a biblioteca
 * react-text-mask
 *
 * @param {Number} maxDigitsBeforeSeparator Quantidade de digitos antes da ','(separador), esse valor não
 * deve ser maior que 3
 * @param {Number} maxDigitsAfterSeparator Quantidade de digitos depois da ','(separador)
 * @param {String} separator Simbolo que será usado para separar os digitos inteiros dos decimais,
 * por padrão é uma virgula
 * @param {String} defaultStartDigit Simbolo que deve ser usado por padrão quando o mesmo não for digitado
 * pelo usuário, esse valor deve ser '+' ou '-', por padrão é o '-'
 */
export const decimalDegreeCordinateMask = (
  maxDigitsBeforeSeparator,
  maxDigitsAfterSeparator,
  separator = ',',
  defaultStartDigit = '-'
) => (rawValue) => {
  let mask = [];
  let separatorInserted = false;
  // Valor iniciao com 1, pois é obrigatório que tenha pelo menos 1 digito antes do separador
  let digitsBeforeSeparator = 1;

  // Verifica se o primeiro digito coincide com + ou -, caso não é inserido o sinal padrão
  if (rawValue.substr(0, 1).match(/(\+|-)/)) mask.push(/(\+|-)/);
  else mask.push(defaultStartDigit);

  /**
   * Aqui é verificado se os valores digitados ultrapassam os valores máximos, caso ultrapassem
   * é retornada uma mascara que vai limitar o usuário para não ultrapassar esses valores, e caso
   * não ultrapassem não será feito nada
   */
  const valueBeforeSeparator = rawValue
    .substr(1, maxDigitsBeforeSeparator)
    .replace(/,\d+/, '');
  if (valueBeforeSeparator >= 90 && maxDigitsBeforeSeparator === 2) {
    mask = [...mask, '9', '0', separator];
    for (let i = 0; i < maxDigitsAfterSeparator; i++) {
      mask.push(/0/);
    }
    return mask;
  }
  if (valueBeforeSeparator >= 180 && maxDigitsBeforeSeparator === 3) {
    mask = [...mask, '1', '8', '0', separator];
    for (let i = 0; i < maxDigitsAfterSeparator; i++) {
      mask.push(/0/);
    }
    return mask;
  }

  // Adicionando digito obrigatório antes do separador
  mask.push(/\d/);

  // Aqui é verificado se o usuário inseriu o separador antes da quantidade de digitos máximos
  rawValue
    .substr(1, 4)
    .split('')
    .forEach((d) => {
      if (d === separator && !separatorInserted) {
        separatorInserted = true;
      } else if (
        d !== separator &&
        !separatorInserted &&
        digitsBeforeSeparator < maxDigitsBeforeSeparator
      ) {
        mask.push(/\d|,/);
        digitsBeforeSeparator++;
      } else if (d !== separator && !separatorInserted) {
        mask.push(separator);
        separatorInserted = true;
      }
    });
  // Complementa o passo anterior caso o usuário não tenha digitado o separador
  if (digitsBeforeSeparator < maxDigitsBeforeSeparator && !separatorInserted) {
    while (digitsBeforeSeparator < maxDigitsBeforeSeparator) {
      mask.push(/\d|,/);
      digitsBeforeSeparator++;
    }
    mask.push(separator);
    separatorInserted = true;
  }

  // Por fim são adicionados os digitos após o separador
  for (let i = 0; i < maxDigitsAfterSeparator; i++) {
    mask.push(/\d/);
  }
  return mask;
};

export const DDMMSSCordinateMask = () => (rawValue) => { // transformar em graus decimais
  let mask = [];
  if (rawValue.substr(0, 1).match(/(\+|-)/)) {
    mask.push(/(\+|-)/);
  } else {
    mask.push('-');
  }
  mask = [...mask, /\d/, /\d/, '°', /\d/, /\d/, "'", /\d/, /\d/, ',', /\d/, /\d/, /\d/, /\d/, '"'];
  return mask;
};

export const UTMCordinateMask = (direction) => (rawValue) => {
  let maxDigitsBeforeSeparator = direction === 'latitude' ? 7 : 6,
    maxDigitsAfterSeparator = 2,
    separator = '.',
    mask = [],
    separatorInserted = false,
    digitsBeforeSeparator = 1; // Valor iniciao com 1, pois é obrigatório que tenha pelo menos 1 digito antes do separador

  // Adicionando digito obrigatório antes do separador
  mask.push(/\d/);

  // Aqui é verificado se o usuário inseriu o separador antes da quantidade de digitos máximos
  rawValue.split('').forEach((d) => {
    if (d === separator && !separatorInserted) {
      separatorInserted = true;
    } else if (
      d !== separator &&
      !separatorInserted &&
      digitsBeforeSeparator < maxDigitsBeforeSeparator
    ) {
      mask.push(/\d|\./);
      digitsBeforeSeparator++;
    } else if (d !== separator && !separatorInserted) {
      mask.push(separator);
      separatorInserted = true;
    }
  });
  // Complementa o passo anterior caso o usuário não tenha digitado o separador
  if (digitsBeforeSeparator < maxDigitsBeforeSeparator && !separatorInserted) {
    while (digitsBeforeSeparator < maxDigitsBeforeSeparator) {
      mask.push(/\d|\./);
      digitsBeforeSeparator++;
    }
    mask.push(separator);
    separatorInserted = true;
  }

  // Por fim são adicionados os digitos após o separador
  for (let i = 0; i < maxDigitsAfterSeparator; i++) {
    mask.push(/\d/);
  }
  return mask;
};

export const phoneNumber = () => (rawValue) => {
  const pureNumbers = rawValue.replace(/\D/gi, '');
  if (pureNumbers.length >= 13) {
    return [
      '[',
      '+',
      /\d/,
      /\d/,
      ']',
      ' ',
      '(',
      /\d/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ];
  } else {
    return [
      '[',
      '+',
      /\d/,
      /\d/,
      ']',
      ' ',
      '(',
      /\d/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ];
  }
};

// Máscara para campos data no formato dd/mm/aaaa
export const dateMask = () => {
  return [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
};
